import { createFileRoute } from "@tanstack/react-router"
import { z } from "zod"

import { CsvExportButton } from "@/components/common/csv-export-button"
import { IndividualsDirectoryToolbar } from "@/features/individuals/components/individuals-directory-toolbar"
import { PageHeader } from "@/components/common/page-header"
import { IndividualsTable } from "@/features/individuals/components/individuals-table"
import { individualStatusLabel } from "@/features/individuals/individual-status-label"
import type { CsvColumn } from "@/lib/csv"
import type { SortOrder } from "@/lib/api/types"
import { individualQueries, loadAllIndividuals } from "@/queries/individuals"
import type { IndividualSort, ListedIndividual } from "@/types/individual"

const searchSchema = z.object({
  q: z.string().optional(),
  groupBy: z.enum(["status"]).optional(),
  status: z.enum(["active", "archived"]).optional(),
  sort: z.enum(["name", "complianceScore", "createdAt"]).optional(),
  order: z.enum(["asc", "desc"]).optional(),
})

const exportColumns: CsvColumn<ListedIndividual>[] = [
  { header: "IRN", value: (person) => person.irn },
  { header: "Name", value: (person) => person.fullName },
  { header: "Email", value: (person) => person.email },
  { header: "Status", value: (person) => individualStatusLabel(person.status) },
  { header: "Nationality", value: (person) => person.nationality },
  {
    header: "Score",
    value: (person) =>
      person.complianceScore === null ? null : `${person.complianceScore}%`,
  },
]

export const Route = createFileRoute(
  "/$organisation/_authenticated/_organisation/_main/individuals/"
)({
  validateSearch: searchSchema,
  loaderDeps: ({ search: { q, status, sort, order } }) => ({
    q,
    status,
    sort,
    order,
  }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureInfiniteQueryData(
      individualQueries.infiniteList(deps)
    ),
  component: IndividualsPage,
})

function IndividualsPage() {
  const { groupBy, ...filters } = Route.useSearch()
  const navigate = Route.useNavigate()

  const sortBy = (sort: IndividualSort, order: SortOrder) => {
    void navigate({
      replace: true,
      search: (current) => ({ ...current, sort, order }),
    })
  }

  return (
    <>
      <PageHeader
        title="People"
        actions={
          <CsvExportButton
            columns={exportColumns}
            filename="individuals.csv"
            loadRows={(signal) => loadAllIndividuals(filters, signal)}
          />
        }
      />
      <section className="flex flex-col gap-4">
        <IndividualsDirectoryToolbar
          filters={filters}
          groupBy={groupBy}
          onChange={(changes) => {
            void navigate({
              replace: true,
              search: (current) => ({ ...current, ...changes }),
            })
          }}
        />
        <IndividualsTable
          filters={filters}
          groupBy={groupBy}
          onSortChange={sortBy}
        />
      </section>
    </>
  )
}
